import {
  applyUpdate,
  type ConversationLanguage,
  type ConversationUpdate,
  type ConversationView,
} from '@cyberscore/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';

/** One action in the conversation, as the core takes it (docs/decisions/0011-conversation-module.md). */
export type ChatAction =
  | { type: 'send'; text: string }
  | { type: 'answer'; question: string; value: unknown; via: 'tapped' | 'form' }
  | { type: 'confirm' | 'decline'; card: string; questions: string[] }
  | { type: 'skip'; question: string }
  | { type: 'language'; language: ConversationLanguage };

function request(cid: string, a: ChatAction): Promise<ConversationUpdate> {
  const base = `/conversations/${cid}`;
  switch (a.type) {
    case 'send':
      return api('POST', `${base}/messages`, { text: a.text });
    case 'answer':
      return api('POST', `${base}/answers`, { question: a.question, value: a.value, via: a.via });
    case 'confirm':
    case 'decline':
      return api('POST', `${base}/cards/${a.card}/${a.type}`, { questions: a.questions });
    case 'skip':
      return api('POST', `${base}/skip`, { question: a.question });
    case 'language':
      return api('PATCH', base, { language: a.language });
  }
}

const key = (tenant: string | undefined, assessment: string) => ['conversation', tenant, assessment] as const;

/**
 * The caller's conversation on an assessment: started (or resumed, which may add a "welcome back" and the
 * next question), then read whole. Kept until the screen acts on it; each action applies its update.
 */
export function useConversation(assessmentId: string) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: key(tenant?.id, assessmentId),
    queryFn: async () => {
      const started = await api<ConversationUpdate>('POST', `/assessments/${assessmentId}/conversation`, {});
      return api<ConversationView>('GET', `/conversations/${started.conversation.id}`);
    },
    enabled: !!tenant,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * Takes an action. Its update is applied to the transcript as soon as it arrives; the assessment's own
 * views (answers, progress) are refreshed after anything that may have saved an answer. A conflict (the
 * conversation moved on in another tab) reloads the whole conversation.
 */
export function useChatAction(assessmentId: string) {
  const qc = useQueryClient();
  const { tenant } = useSession();
  const k = key(tenant?.id, assessmentId);
  return useMutation({
    mutationFn: (a: ChatAction) => {
      const view = qc.getQueryData<ConversationView>(k);
      if (!view) throw new Error('The conversation has not loaded yet');
      return request(view.conversation.id, a);
    },
    onSuccess: (update) =>
      qc.setQueryData<ConversationView>(k, (view) => (view ? applyUpdate(view, update) : view)),
    onSettled: (_data, error, a) => {
      if (error && 'status' in error && error.status === 409) void qc.invalidateQueries({ queryKey: k });
      if (a.type !== 'language') void qc.invalidateQueries({ queryKey: ['assessments', tenant?.id, assessmentId] }); // prettier-ignore
    },
  });
}
